/**
 * タブで切り替える設定ページの枠。
 *
 * 設定項目は増え続けるので、縦に積んでスクロールさせると
 * 目的の項目に辿り着けなくなる。節を足すだけで増やせるようにしておく。
 *
 * 選んだタブは localStorage に覚える。設定は「直したい項目を何度も往復する」
 * 使い方になるので、毎回先頭に戻されると地味に効く。
 *
 * 【キーボード操作】
 * WAI-ARIA の tabs パターンに従う。左右キーでタブを移動でき、
 * Home/End で端へ飛ぶ。タブ自体は roving tabindex（選択中だけ Tab で拾う）。
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'

export type TabDef = {
  id: string
  label: string
  /** 未設定などの注意を示す小さな印。 */
  badge?: ReactNode
  /**
   * 一度表示したら、別のタブに移っても hidden で描き続ける（入力中の下書きを失わない）。
   * 未訪問のタブは従来どおり描かない（重い初期化を裏で走らせない）。
   */
  keepMounted?: boolean
  render: () => ReactNode
}

export function TabbedPanel({
  tabs,
  storageKey,
  label = '設定',
}: {
  tabs: TabDef[]
  /** 選択状態の保存先。画面ごとに変える。省略すると保存しない（毎回先頭のタブから）。 */
  storageKey?: string
  /** タブ列の読み上げ名。 */
  label?: string
}) {
  const [active, setActive] = useState<string>(() => {
    if (typeof window === 'undefined' || !storageKey) return tabs[0]?.id ?? ''
    const saved = window.localStorage.getItem(storageKey)
    // 保存されたタブが無くなっていることがある（機能の入れ替え）
    return saved && tabs.some((t) => t.id === saved) ? saved : (tabs[0]?.id ?? '')
  })
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})
  // 一度でも表示したタブ。keepMounted のものはここに入っていれば描き続ける
  const [visited, setVisited] = useState<Set<string>>(() => new Set(active ? [active] : []))
  useEffect(() => {
    setVisited((prev) => (prev.has(active) ? prev : new Set(prev).add(active)))
  }, [active])

  useEffect(() => {
    if (!storageKey) return
    try {
      window.localStorage.setItem(storageKey, active)
    } catch {
      // プライベートブラウジング等で書けない。選択が保存されないだけ。
    }
  }, [active, storageKey])

  const move = (delta: number) => {
    const i = tabs.findIndex((t) => t.id === active)
    const next = tabs[(i + delta + tabs.length) % tabs.length]
    if (!next) return
    setActive(next.id)
    refs.current[next.id]?.focus()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      move(1)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      move(-1)
    } else if (e.key === 'Home') {
      e.preventDefault()
      const first = tabs[0]
      if (first) {
        setActive(first.id)
        refs.current[first.id]?.focus()
      }
    } else if (e.key === 'End') {
      e.preventDefault()
      const last = tabs[tabs.length - 1]
      if (last) {
        setActive(last.id)
        refs.current[last.id]?.focus()
      }
    }
  }

  const current = tabs.find((t) => t.id === active) ?? tabs[0]

  return (
    <div className="tabbed">
      <div className="tabbed-bar" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
        {tabs.map((tab) => {
          const selected = tab.id === current?.id
          return (
            <button
              key={tab.id}
              ref={(el) => {
                refs.current[tab.id] = el
              }}
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`panel-${tab.id}`}
              // 選択中のタブだけを Tab キーの移動先にする（ARIA の推奨）
              tabIndex={selected ? 0 : -1}
              className={selected ? 'tabbed-tab active' : 'tabbed-tab'}
              onClick={() => setActive(tab.id)}
            >
              {tab.label}
              {tab.badge !== undefined && <span className="tabbed-badge">{tab.badge}</span>}
            </button>
          )
        })}
      </div>

      {/* 表示中のタブだけを描く（全部描くと重い初期化が裏で走る）。
          keepMounted のタブは一度表示したら hidden で残す。 */}
      {tabs
        .filter((tab) => tab.id === current?.id || (tab.keepMounted && visited.has(tab.id)))
        .map((tab) => (
          <div
            key={tab.id}
            className="tabbed-panel"
            role="tabpanel"
            id={`panel-${tab.id}`}
            aria-labelledby={`tab-${tab.id}`}
            tabIndex={0}
            hidden={tab.id !== current?.id}
          >
            {tab.render()}
          </div>
        ))}
    </div>
  )
}
